import * as React from 'react'
import { forwardRef, useCallback, useState } from 'react'

import { useDroppable } from '@dnd-kit/core'
import clsx from 'clsx'

import { format } from '~publish/helpers/temporal'

import { AddButton } from './AddButton'
import { useCalendarContext } from './context'
import { DraggableAndDroppableItem } from './DraggableAndDroppableItem'
import { DraggableItem } from './DraggableItem'
import { DroppableItem } from './DroppableItem'
import { Item } from './Item'
import ShowMoreLessButtons from './ShowMoreLessButtons'
import type { CalendarBlockData } from './types'

import styles from './Day.module.css'

const DEFAULT_ITEMS_LIMIT = 3
const FIVE_MINUTES_MS = 300000

interface DayProps {
  day: CalendarBlockData
}

const Day = forwardRef<HTMLTableCellElement, DayProps>(({ day }, ref) => {
  const {
    itemsByDay,
    selectedDate,
    loading,
    addItemProps,
    currentTime,
    isMobile,
    isCompact,
    readOnly,
  } = useCalendarContext()
  const { now, today, todayTimestamp, nowTimestamp } = currentTime

  const [limit, setLimit] = useState(DEFAULT_ITEMS_LIMIT)

  const dropDisabled = day.startTimestamp < todayTimestamp

  const isToday =
    day.start.day === today.day &&
    day.start.month === today.month &&
    day.start.year === today.year
  const isCurrentMonth = day.start.month === selectedDate.month
  const dayId = day.startTimestamp.toString()

  const itemsToRender = itemsByDay?.get(day.startTimestamp)

  const getCreationTimestamp = useCallback(
    (): number =>
      day.creationTimestamp < nowTimestamp
        ? nowTimestamp + FIVE_MINUTES_MS
        : day.creationTimestamp,
    [day.creationTimestamp, nowTimestamp],
  )

  const { setNodeRef, isOver, active } = useDroppable({
    id: dayId ?? '',
    disabled: dropDisabled,
    data: {
      type: 'hour',
      timestamp: day.startTimestamp,
      creationTimestamp: getCreationTimestamp(),
    },
  })

  const showAddButton =
    !readOnly && !dropDisabled && !active && !loading && !isMobile

  const isClickable = isMobile && !readOnly && !dropDisabled && !loading

  const handleDayClick = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>): void => {
      if (!isClickable) return

      // Only trigger when tapping empty space in the cell directly
      if (event.target !== event.currentTarget) return

      addItemProps.onClick(getCreationTimestamp())
    },
    [isClickable, addItemProps, getCreationTimestamp],
  )

  const handleDayKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>): void => {
      if (!isClickable || loading) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        addItemProps.onClick(getCreationTimestamp())
      }
    },
    [isClickable, loading, addItemProps, getCreationTimestamp],
  )

  const dayLabel = format(day.start, 'eeee, d MMMM y')

  return (
    <td
      data-testid={`monthly-day-${day}`}
      data-datetime={day.toString()}
      data-container-id={dayId}
      className={clsx(styles.day, {
        [styles.disabled]: dropDisabled,
        [styles.active]: isToday,
        [styles.draggingOver]: isOver,
        [styles.currentMonth]: isCurrentMonth,
        [styles.dragHappening]: Boolean(active),
        [styles.clickable]: isClickable,
      })}
      ref={ref}
      onClick={handleDayClick}
      onKeyDown={handleDayKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Create post on ${dayLabel}` : undefined}
    >
      <span className={styles.dayNumber}>{day.start.day}</span>
      {day.startTimestamp && showAddButton && (
        <AddButton
          data-testid={`post-button-${day.start.toString()}`}
          labelDate={format(day.start, 'eeee, d MMMM y') ?? ''}
          timestamp={getCreationTimestamp()}
          className={styles.addButton}
        />
      )}
      <div className={styles.dayContent} ref={setNodeRef}>
        {day.startTimestamp && now && (
          <>
            <div
              className={clsx(styles.itemsWrapper, {
                [styles.mobileGrid]: isMobile || isCompact,
              })}
            >
              {itemsToRender?.slice(0, limit).map((item, index) => {
                if (item.draggable && item.droppable) {
                  return (
                    <DraggableAndDroppableItem
                      key={item.id}
                      id={item.id}
                      index={index}
                      timestamp={item.timestamp}
                      type={item.type}
                      disabled={
                        item.draggable === undefined
                          ? item.timestamp <
                            Math.floor(now.epochMilliseconds / 1000)
                          : !item.draggable
                      }
                    >
                      <Item {...item} />
                    </DraggableAndDroppableItem>
                  )
                }
                if (item.draggable) {
                  return (
                    <DraggableItem
                      key={item.id}
                      id={item.id}
                      index={index}
                      timestamp={item.timestamp}
                      type={item.type}
                      className={clsx(styles.item, {
                        [styles.activeItem]: active?.id === item.id,
                      })}
                      disabled={
                        item.draggable === undefined
                          ? item.timestamp <
                            Math.floor(now.epochMilliseconds / 1000)
                          : !item.draggable
                      }
                    >
                      <Item {...item} />
                    </DraggableItem>
                  )
                }
                if (item.droppable) {
                  return (
                    <DroppableItem
                      id={item.id}
                      key={item.id}
                      index={index}
                      timestamp={item.timestamp}
                      type={item.type}
                    >
                      <Item {...item} />
                    </DroppableItem>
                  )
                }
                return <Item {...item} key={item.id} />
              })}
            </div>
            <ShowMoreLessButtons
              itemsToRender={itemsToRender}
              limit={limit}
              className={styles.showMoreButton}
              setLimit={setLimit}
              compact={isMobile || isCompact}
            />
          </>
        )}
      </div>
    </td>
  )
})

Day.displayName = 'Day'

const memoDay = React.memo(Day)
export { memoDay as Day }
